import { Box, Flex, Grid, GridItem, Text, Image } from '@chakra-ui/react';
import TodayRequestIcon from "../../../assets/images/TodayRequestIcon.svg";
import PendingApprovalsIcon from '../../../assets/images/PendingApprovalsIcon.svg';
import RejectRequestsIcon from '../../../assets/images/RejectedRequestsIcon.svg';
import ApprovedLeavesIcon from '../../../assets/images/ApprovedLeavesIcon.svg';
import MyDiv from './regularisation.style';
import { RegularisationList } from '../components';
import { useRef, useState } from 'react';
import { CustomToast } from '../../../components';
import { useFetch } from '../../../hooks/useFetch';
import { useEffect } from 'react';
import { IFilterOptions } from '../../../components/FilterCard/filterCard';
import { ILazyParams } from '../../../components/CustomTable/CustomTables';

export interface RegularizationData {
  id: number;
  empId: number;
  employee_Code?: string;
  name: string;
  dateOfRegularisation: string;
  inTime: string;
  outTime: string;
  reason: string;
  status: boolean | null;
  upadtedByName: string;
  updated_At: string;
  rejected_Reason: string;
  canApprove: boolean;
  proofExists?: boolean;
  attendanceStatus: number;
  managerName?: string;
  departementName?: string;
  companyName?: string;
  branchName?: string;
  totalWorkingHours?: number;
  totalWorkingHoursFormatted?: string;
  expectedStatus?: number;
  expectedStatusName?: string;
}
export interface CardData {
  todayLeave?: number;
  totalCount?: number;
  pendingCount: number;
  approvedCount: number;
  rejectedCount: number;
}

type RegularisationLazyParams = ILazyParams & { from?: string; to?: string };

const Regularisation = () => {
  const { addToast } = CustomToast();
  const { fetchApi, loading } = useFetch(addToast);
  const [employees, setEmployees] = useState<RegularizationData[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [alertStatus, setAlertStatus] = useState<boolean | null>(null);
  const [cardData, setCardData] = useState<CardData>({ totalCount: 0, pendingCount: 0, approvedCount: 0, rejectedCount: 0, });
  const [selectedStatus, setSelectedStatus] = useState<number[]>([]);
  const [lazyParams, setLazyParams] = useState<RegularisationLazyParams>({ first: 0, rows: 20, sortField: undefined, sortOrder: undefined, multiSortMeta: [], filters: {}, globalFilter: '', });
  const [totalCount, setTotalCount] = useState<number>(0);
  const [rejectReason, setRejectReason] = useState("");
  const [approveReason, setApproveReason] = useState("");
  const [loadingState, setLoadingState] = useState(false);
  const [filter, setFilter] = useState<IFilterOptions>({ filterOptions: {} });
  const [appliedFilter, setAppliedFilter] = useState<Record<string, number[]>>({});

  const rangeRef = useRef<{ from?: string; to?: string }>({});

  useEffect(() => {
    if (lazyParams.from && lazyParams.to) {
      rangeRef.current = { from: lazyParams.from, to: lazyParams.to };
    }
  }, [lazyParams]);

  const getRange = () => ({
    from: lazyParams.from ?? rangeRef.current.from,
    to: lazyParams.to ?? rangeRef.current.to,
  });

  useEffect(() => {
    const { from, to } = getRange();
    if (!from || !to) return;
    const timeout = setTimeout(() => { LoadData(); }, 200);
    return () => clearTimeout(timeout);
  }, [searchTerm, lazyParams, selectedStatus, appliedFilter]);

  const toggleRow = (id: number) => {
    setSelectedRows(prev =>
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    const pendingIds = employees.filter(emp => emp.status === null && emp.canApprove).map(emp => emp.id);
    const allSelected = pendingIds.every(id => selectedRows.includes(id));

    if (allSelected) {
      setSelectedRows(prev => prev.filter(id => !pendingIds.includes(id)));
    } else {
      setSelectedRows(prev => [...new Set([...prev, ...pendingIds])]);
    }
  };

  const handleStatusChange = (status: boolean | null) => {
    setAlertStatus(status);
  };

  const confirmStatusChange = async () => {
    if (selectedRows.length === 0 || alertStatus === null) return;
    setLoadingState(true);

    if (alertStatus === false && (!rejectReason || rejectReason.trim() === "")) {
      addToast({ message: "Reject reason is required", status: "warning" });
      setLoadingState(false);
      return;
    }
    if (alertStatus === true && (!approveReason || approveReason.trim() === "")) {
      addToast({ message: "Approve reason is required", status: "warning" });
      setLoadingState(false);
      return;
    }

    const payload: any = {
      RegulariseIds: selectedRows,
      Status: alertStatus,
    };

    if (alertStatus === false) {
      payload.rejectReason = rejectReason.trim();
    }
    if (alertStatus === true) {
      payload.rejectReason = approveReason.trim();
    }

    try {
      const response = await fetchApi('Regularization', 'PUT', payload);
      if (response && response.message) {
        addToast({ message: response.message, status: 'success' });
        setRejectReason("");
        setApproveReason("");
        await LoadData();
      }
    } catch (error: any) {
      addToast({ message: error.message || 'An error occurred', status: 'error' });
    } finally {
      setAlertStatus(null);
      setSelectedRows([]);
      setLoadingState(false);
    }
  };

  async function loadRegularizationFilter() {
    const response = await fetchApi<IFilterOptions>('Filters/ManagerDepartement', 'GET', null, null, '');
    if (response) {
      setFilter(response);
    }
  }

  const LoadData = async () => {
    try {
      const params = new URLSearchParams();
      params.set('first', String(lazyParams.first));
      params.set('rows', String(lazyParams.rows));
      const { from, to } = getRange();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (lazyParams.sortField) params.set('sortField', lazyParams.sortField);
      if (lazyParams.sortOrder) params.set('sortOrder', String(lazyParams.sortOrder));
      const search = searchTerm.trim() || lazyParams.globalFilter;
      if (search) params.set('globalFilter', search);

      if (lazyParams.filters && Object.keys(lazyParams.filters).length) {
        params.set('filters', JSON.stringify(lazyParams.filters));
      }
      if (Object.keys(appliedFilter).length) {
        params.set('filter', JSON.stringify(appliedFilter));
      }

      const data = await fetchApi('Regularization', 'GET', null, params.toString());
      if (data) {
        setEmployees(data.result ?? []);
        setTotalCount(data.totalcount ?? data.Totalcount ?? 0);
        if (data.summary) setCardData(data.summary);
      }
    } catch (error: any) {
      addToast({ message: error.message || 'An error occurred', status: 'error' });
    }
  };

  const cards = [
    { label: "Total Requests", icon: TodayRequestIcon, count: cardData.totalCount || 0 },
    { label: "Pending Requests", icon: PendingApprovalsIcon, count: cardData.pendingCount || 0 },
    { label: "Approved Requested", icon: ApprovedLeavesIcon, count: cardData.approvedCount || 0 },
    { label: "Rejected Requested", icon: RejectRequestsIcon, count: cardData.rejectedCount || 0 },
  ];

  useEffect(() => {
    loadRegularizationFilter();
  }, []);

  return (
    <MyDiv>
      <Box className="page_wrapper">
        <Flex className="page_heading flex_header">
          <Text as="h1" className="font-poppins font_dark text_semibold text_4xl">Regularisation</Text>
        </Flex>

        <Grid className="grid_container grid_gap_sm" mt={4}>
          {cards.map((card, index) => (
            <GridItem key={index} colSpan={{ base: 12, sm: 6, md: 6, lg: 3 }} className="card_box">
              <Flex align="center" gap={3} mb={3}>
                <Image src={card.icon} className="card_icon" alt={card.label} />
                <Text className="card_label font-poppins text_md text_medium">{card.label}</Text>
              </Flex>
              <Text className="card_value font-poppins text_bold">{card.count}</Text>
            </GridItem>
          ))}
        </Grid>
      </Box>
      <Grid className="grid_container" mt={4}>
        <GridItem colSpan={12}>
          <RegularisationList
            filter={filter} approveReason={approveReason} setAppliedFilter={setAppliedFilter} setApproveReason={setApproveReason} rejectReason={rejectReason} setRejectedReason={setRejectReason} employees={employees}
            selectedRows={selectedRows} setSelectedRows={setSelectedRows} setAlertStatus={setAlertStatus} toggleRow={toggleRow} toggleAll={toggleAll}
            handleStatusChange={handleStatusChange} alertStatus={alertStatus} confirmStatusChange={confirmStatusChange} setSearchTerm={setSearchTerm} searchTerm={searchTerm}
            selectedStatus={selectedStatus} setSelectedStatus={setSelectedStatus} lazyParams={lazyParams} setLazyParams={setLazyParams} totalCount={totalCount} loading={loading}
            loadingState={loadingState} setLoadingState={setLoadingState}
          />
        </GridItem>
      </Grid>
    </MyDiv>
  );
};

export default Regularisation;